<template>
	<Page color="neutralLight" no-sidebar :no-top-page="cart.data.items.length === 0">
		<v-container class="py-md-4 pa-print-0">
			<v-stepper data-testid="breadcrumb" v-model="stepIndex" class="d-print-none">
				<v-stepper-header>
					<template v-for="(item, itemIdx) in _steps">
						<v-divider :key="itemIdx + '_div'" v-if="itemIdx > 0"></v-divider>
						<v-stepper-step :editable="stepIndex > itemIdx + 1"
										edit-icon="mdi-check"
										:key="itemIdx + '_item'"
										:complete="stepIndex > itemIdx + 1"
										:step="itemIdx + 1"
										:rules="[() => !item.warning]">
							<span v-text="$t('cart.' + item.key + '.title')"></span>
						</v-stepper-step>
					</template>
				</v-stepper-header>
			</v-stepper>

			<v-skeleton-loader v-if="skeleton" type="image" class="mt-8"></v-skeleton-loader>
			<component v-else v-bind="_steps[stepIndex -1]"
				:cart.sync="cart"
				:payments.sync="payments"
				:loading="loading"
				@next="handleNext"
				@partialPaymentCompleted="handlePartialPaymentCompleted"
			/>
		</v-container>
	</Page>
</template>

<script>
	import Page from "@/components/Page";
	import Welcome from "@/routes/App/Cart/Welcome";
	import Identification from "@/routes/App/Cart/Identification";
	import Assignment from "@/routes/App/Cart/Assignment";
	import Payment from "@/routes/App/Cart/Payment";
	import {
		EComService,
		CartModel,
		EventBus,
		BaseModel,
		AccountModel,
		storage
	} from "@connectngo/sdk";
	import CompleteAccountForm from "@/components/CompleteAccountForm";
	import ConfigFieldsMixin from "@/mixins/ConfigFieldsMixin";
    import ReloadV2Mixin from '@/mixins/ReloadV2Mixin'
	import GoogleAnalyticsMixin from '@/mixins/GoogleAnalyticsMixin'
	import globalVariables from '@/global'

	export default {
		name: "Cart",

		mixins: [ConfigFieldsMixin, ReloadV2Mixin, GoogleAnalyticsMixin],

		components: { Page },
		metaInfo() {
			return {
				title: this.$i18n.t("cart.welcome.title"),
				meta: [
					{
						property: "og:title",
						content: this.$i18n.t("cart.welcome.title"),
					},
					{ property: "og:url", content: window.location.href },
				],
			};
		},
		data: () => ({
			loading: false,
			skeleton: true,
			stepIndex: 1,
			steps: [
				{ key: "welcome", is: Welcome, class: "mt-8 pa-0", enabled: true },
				{
					key: "identification",
					is: Identification,
					style: "max-width: 30rem",
					class: "mt-8 pa-0",
					enabled: true
				},
				{
					key: "account_fields",
					is: CompleteAccountForm,
					style: "max-width: 25rem",
					class: "mt-8 pa-0",
					enabled: false,
					warning: true,
				},
				{
					key: "assignment",
					is: Assignment,
					class: "mt-8 pa-0",
					enabled: false,
				},
				{ key: "payment", is: Payment, class: "mt-8 pa-0" },
				{ key: "reload", disabled: true},
				{ key: "confirmation", disabled: true },
			],
			cart: new CartModel(),
			payments: new BaseModel(),
		}),

		computed: {
			_steps() {
				return this.steps.filter((step) => step.enabled !== false && step.key !== 'reload');
			},
			accountRequiredModifiersFeatureFlagEnabled() {
				const { feature_flags } = this.$root.websiteConfig.data || {};
				return feature_flags && feature_flags.includes('dev-11522-account-required-modifiers');
			},
		},
		watch: {
			stepIndex(value) {
				const step = this._steps[value - 1] || {};
				if (step.key !== this.$route.params.step && step.enabled) {
					this.goToStep(step.key);
				}
			},
		},

		methods: {
			goToStep(key) {
				const targetRoute = { name: "cart_step", params: { step: key } };

				if (this.$route.name !== targetRoute.name || this.$route.params.step !== targetRoute.params.step) {
					this.stepIndex = this.getEnabledStepIndexByKey(key) + 1;
					if (key === 'payment') {
						this.triggerCheckoutGAEvent()
					}

					this.$router.push(targetRoute);
				}
			},
			handleNext() {
				const step = this._steps[this.stepIndex];
				if (step) {
					this.goToStep(step.key);
				}
			},
			handlePartialPaymentCompleted() {
				this.load();
			},
			load() {
				this.loading = true;

				if (true === globalVariables.websiteConfig.featureFlagEnabled(
					'co-9545-adding-transaction-uuid-in-adyen-return-url'
				)) {
					this.cartUuidStillPresent();
				}

				return new EComService()
					.getCart()
					.then((cart) => {
						cart.data.amountToPay = cart.data.balance;
						return new EComService()
							.getCartPayments(cart.data.id)
							.then((payments) => {
								Object.assign(this, { cart, payments });
								if (this.cartHasAccountRequiredItems(cart)) {
									this.enableStep("assignment");
								}
								this.skeleton = false;
							});
					})
					.catch((reason) => {
						new EComService().logout().then(() => {
							if (reason.response.status === 422) {
								this.$snack(this.$i18n.t("error.session_expired"), "warning");
							} else {
								this.$handleError(this, reason);
							}
							this.$root.user.data = new AccountModel().data;
							this.storage.remove("cartId");
							this.load();
						});
					})
					.finally(() => (this.loading = false));
			},
			getStepByKey(key) {
				return this.steps.find((step) => step.key === key);
			},
			getEnabledStepIndexByKey(key) {
				return this._steps.findIndex((step) => step.key === key);
			},
			enableStep(key) {
				this.getStepByKey(key).enabled = true;
			},
			cartHasAccountRequiredItems(cart) {
				if (this.accountRequiredModifiersFeatureFlagEnabled) {
					return cart.data.items.some((item) => {
						if (item.data.comboItems?.length > 0) {
							return item.data.comboItems.some((combo) => combo.product.account_required === 1)
						}

						// Only return true to allow the base product check to run when no modifier matches
						if (item.data.modifiers?.some((modifier) => modifier.data.product.data.account_required === 1)) {
							return true
						}

						return item.data.product.data.account_required === 1;
					});
				}

				for (const item of cart.data.items) {
					if (
						item.data.product.data.account_required === 1 ||
						item.data.comboItems.some(combo => combo.product.account_required === 1)
					) {
						return true;
					}
				}

				return false;
			},
			cartUuidStillPresent() {
				const cartUuid = storage.get('cartId');

				const queryString = window.location.search;
				const urlParams = new URLSearchParams(queryString);
				const transactionUuid = urlParams.get('transactionuuid');
				if (cartUuid === undefined && transactionUuid) {
					storage.set('cartId', transactionUuid);
				}
			},
		},

		created() {
			if (this.$route.params.step === 'account_fields') {
				this.enableStep(this.$route.params.step);
			}

			this.load().then(() => {
				const index = this.getEnabledStepIndexByKey(this.$route.params.step);
				if (index !== -1) {
					this.stepIndex = index + 1;
				}

				if (this.stepIndex === 1) {
					// first step in the cart then we trigger the cart GA event
					this.triggerCartGAEvent();
				}
			});

			EventBus.subscribe('MISSING_ACCOUNT_FIELDS', () => {
				this.enableStep("account_fields");
				this.goToStep("account_fields");
			});

			EventBus.subscribe("CART_UPDATED", (model) => {
				this.cart = model;
			});

			EventBus.subscribe("GUEST_LOGGED_IN", () => {
				// Go on next step after guest login
				this.handleNext()
			});

			EventBus.subscribe("LOGOUT", () => {
				const identificationStep = this.getEnabledStepIndexByKey("identification") + 1;
				if (this.stepIndex > identificationStep) this.stepIndex = identificationStep;
			});
		},
	};
</script>

